import React, { useEffect, useState } from 'react';
import './InitialLoadGate.css';

const EXIT_ANIMATION_MS = 1100;
const FINAL_REVEAL_MS = 1850;
const MIN_VISIBLE_MS = 1200;
const LOADER_COOLDOWN_MS = 45 * 60 * 1000;
const LAST_SHOWN_KEY = 'zenit_marketing_initial_loader_last_shown';
const TITLE_TEXT = 'ZENIT AI LAB';
const TITLE_CHARACTERS = TITLE_TEXT.split('');

const shouldShowInitialLoader = () => {
  try {
    const lastShownRaw = window.localStorage.getItem(LAST_SHOWN_KEY);
    const lastShownAt = Number(lastShownRaw);

    if (Number.isFinite(lastShownAt) && Date.now() - lastShownAt < LOADER_COOLDOWN_MS) {
      return false;
    }

    window.localStorage.setItem(LAST_SHOWN_KEY, String(Date.now()));
    return true;
  } catch (error) {
    return true;
  }
};

const waitForPageLoad = () => {
  if (document.readyState === 'complete') {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    window.addEventListener('load', resolve, { once: true });
  });
};

const waitForFonts = () => {
  if (document.fonts && document.fonts.ready) {
    return document.fonts.ready.catch(() => Promise.resolve());
  }

  return Promise.resolve();
};

const InitialLoadGate = ({ children }) => {
  const [shouldShowLoader] = useState(shouldShowInitialLoader);
  const [phase, setPhase] = useState(shouldShowLoader ? 'loading' : 'done');

  useEffect(() => {
    if (!shouldShowLoader) {
      return;
    }

    let isMounted = true;

    const minimumDisplay = new Promise((resolve) => {
      setTimeout(resolve, MIN_VISIBLE_MS);
    });

    Promise.all([waitForPageLoad(), waitForFonts(), minimumDisplay]).then(() => {
      if (!isMounted) {
        return;
      }

      setPhase('exiting');

      setTimeout(() => {
        if (isMounted) {
          setPhase('revealing');
        }
      }, EXIT_ANIMATION_MS);

      setTimeout(() => {
        if (isMounted) {
          setPhase('done');
        }
      }, EXIT_ANIMATION_MS + FINAL_REVEAL_MS);
    });

    return () => {
      isMounted = false;
    };
  }, [shouldShowLoader]);

  const isExiting = phase === 'exiting';
  const isGateVisible = phase === 'loading' || phase === 'exiting';
  const appShellClassName = `initial-load-app-shell ${phase === 'revealing' ? 'initial-load-app-shell--revealing' : ''} ${phase === 'done' ? 'initial-load-app-shell--done' : ''}`;

  return (
    <>
      <div className={appShellClassName}>{children}</div>

      {isGateVisible && (
        <div className={`initial-load-gate ${isExiting ? 'initial-load-gate--exit' : ''}`}>
          <div className="initial-load-gate__ambient" />
          <div className="initial-load-gate__aurora" />
          <div className="initial-load-gate__grain" />

          <div className="initial-load-gate__content">
            <p className="initial-load-gate__title" aria-label={TITLE_TEXT}>
              {TITLE_CHARACTERS.map((char, index) => (
                <span
                  key={`${char}-${index}`}
                  className={`initial-load-gate__title-char ${char === ' ' ? 'initial-load-gate__title-char--space' : ''}`}
                  style={{ animationDelay: `${index * 55}ms` }}
                >
                  {char === ' ' ? '\u00A0' : char}
                </span>
              ))}
            </p>
            <div className="initial-load-gate__bar" aria-hidden="true">
              <span className="initial-load-gate__bar-fill" />
              <span className="initial-load-gate__bar-head" />
              <span className="initial-load-gate__bar-glow" />
            </div>
          </div>

          <div className="initial-load-gate__transition-pulse" />
          <div className="initial-load-gate__transition-ribbon" />
          <div className="initial-load-gate__transition-orbit" />
          <div className="initial-load-gate__transition-soft-wipe" />
        </div>
      )}
    </>
  );
};

export default InitialLoadGate;
